import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Star, CheckCircle, Shield } from 'lucide-react';
import Button from '../components/ui/Button';

type FAQ = { question: string; answer: string };
type Testimonial = { quote: string; name: string; role: string };

const screenshots = [
  {
    src: '/screenshots/search_and_see_your_cellar_and_collections-400.webp',
    srcSet: '/screenshots/search_and_see_your_cellar_and_collections-400.webp 400w',
    alt: 'Cellar overview',
    caption: 'Cellar overview',
    width: 1200,
    height: 2539,
  },
  {
    src: '/screenshots/keep_a_diary_of_smoking_notes-400.webp',
    srcSet: '/screenshots/keep_a_diary_of_smoking_notes-400.webp 400w',
    alt: 'Session journal',
    caption: 'Session journal',
    width: 1200,
    height: 2539,
  },
  {
    src: '/screenshots/create_checkin_cards-400.webp',
    srcSet: '/screenshots/create_checkin_cards-400.webp 400w',
    alt: 'Check-ins',
    caption: 'Check-ins',
    width: 1200,
    height: 2539,
  },
  {
    src: '/screenshots/easily_add_tobacco_to_your_cellar-400.webp',
    srcSet: '/screenshots/easily_add_tobacco_to_your_cellar-400.webp 400w',
    alt: 'Add to cellar',
    caption: 'Add to cellar',
    width: 1200,
    height: 2539,
  },
  {
    src: '/screenshots/find_smoking_spots-400.webp',
    srcSet: '/screenshots/find_smoking_spots-400.webp 400w',
    alt: 'Map & spots',
    caption: 'Map & spots',
    width: 1200,
    height: 2539,
  },
];

const testimonials: Testimonial[] = [
  { quote: '“A pleasure to use and add my tobacco to.”', name: 'Alex M.', role: 'Pipe enthusiast' },
  { quote: '“Logging sessions is effortless—and fun.”', name: 'Jamie L.', role: 'Cigar aficionado' },
  { quote: '“Perfect for keeping track of tins and reminders.”', name: 'Ravi S.', role: 'Collector' },
];

const faqs: FAQ[] = [
  {
    question: 'Does Ember work on Android and iOS?',
    answer: 'Yes. It’s a PWA—add to home screen on Android/iOS and use it like a native app.',
  },
  {
    question: 'Can I import or export my cellar?',
    answer: 'Yes. Export to CSV and import your blends to get started fast.',
  },
  {
    question: 'Is there auto-fill or a catalog for quick adds?',
    answer: 'Yes. Use our catalog search to auto-fill blend details and speed up cellar entries.',
  },
];

export default function LandingPagePreview() {
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const [showReplay, setShowReplay] = useState(false);

  function scrollToLogin() {
    document.getElementById('previewLogin')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)', color: '#f5f5f5' }}>
      {/* Hero */}
      <section
        style={{
          padding: '3rem 1.25rem 2rem',
          maxWidth: '1180px',
          margin: '0 auto',
          display: 'grid',
          gap: '2rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          alignItems: 'center',
          justifyItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            maxWidth: '620px',
            margin: '0 auto',
            textAlign: 'center',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Flame size={48} color="var(--color-accent)" />
            <span style={{ color: '#cfcfcf', fontWeight: 600, letterSpacing: '0.02em' }}>
              Ember • 200+ enthusiasts
            </span>
          </div>
          <h1
            style={{
              fontSize: 'clamp(2.25rem, 5vw, 3.25rem)',
              lineHeight: 1.1,
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: 0,
              textAlign: 'center',
            }}
          >
            Never lose track of your pipe & cigar cellar — the pipe smoking app for enthusiasts
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#cfcfcf', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
            For pipe & cigar lovers who want a trusted pipe smoking app for stress-free cellar tracking, session logging,
            reminders, and a community that speaks your language.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '420px' }}>
            <Button
              variant="primary"
              size="large"
              onClick={scrollToLogin}
              className="hero-cta"
              style={{ width: '100%', maxWidth: '360px', padding: '0.85rem 1.25rem', fontWeight: 700, lineHeight: '1.5', fontSize: '1.15rem', margin: '0 auto' }}
            >
              Start free
            </Button>
            <a
              href="https://www.instagram.com/ember_and_leaf/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                color: '#fff',
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.5',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '360px',
                margin: '0 auto',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
              Follow on Instagram
            </a>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '0.35rem 0.75rem',
                alignItems: 'center',
                justifyItems: 'start',
                color: '#cfcfcf',
                fontSize: '0.95rem',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <CheckCircle size={16} color="var(--color-accent)" /> Track cellar & sessions
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <CheckCircle size={16} color="var(--color-accent)" /> Android & iOS
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Star size={16} color="var(--color-accent)" /> Log sessions & ratings
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Shield size={16} color="var(--color-accent)" /> Export/Import via CSV
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel (moved above video to surface pre-fold) */}
      <section style={{ padding: '1rem 1.25rem 3rem', maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: 0,
              textAlign: 'center',
            }}
          >
            See the app in action
          </h2>
        </div>
        <div
          className="carousel-strip"
          style={{
            display: 'flex',
            gap: '1.2rem',
            overflowX: 'auto',
            padding: '0 1.2rem 0.6rem',
            scrollPadding: '1.2rem',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {screenshots.map((shot) => (
            <div
              key={shot.src}
              style={{
                scrollSnapAlign: 'start',
                scrollMargin: '1.2rem',
                flex: '0 0 clamp(270px, 70vw, 380px)',
                maxWidth: '380px',
                borderRadius: '12px',
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <img
                src={shot.src}
                srcSet={shot.srcSet}
                sizes="(max-width: 768px) 90vw, 360px"
                alt={shot.alt}
                width={shot.width}
                height={shot.height}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <div style={{ padding: '0.5rem 0', color: '#cfcfcf', fontWeight: 600, textAlign: 'center' }}>
                {shot.caption}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hero video moved below carousel */}
      <section style={{ padding: '0 1.25rem 3rem', maxWidth: '1180px', margin: '0 auto' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            border: 'none',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
            maxWidth: '560px',
            width: '100%',
            margin: '0 auto',
            aspectRatio: '540 / 1211',
          }}
        >
          {/* Poster-first paint; fade video in once ready */}
          <img
            src="/screenshots/ember_demo_poster-400.webp"
            srcSet="/screenshots/ember_demo_poster-400.webp 400w"
            sizes="(max-width: 768px) 90vw, 400px"
            fetchPriority="high"
            alt="Ember app cellar preview"
            width={1080}
            height={2422}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '16px',
              opacity: videoReady ? 0 : 1,
              transition: 'opacity 200ms ease',
            }}
            decoding="async"
          />
          <video
            ref={heroVideoRef}
            src="/screenshots/ember_demo.mp4"
            poster="/screenshots/ember_demo_poster-400.webp"
            autoPlay
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={() => {
              if (heroVideoRef.current) {
                // Play at 1.25× for a more relaxed pace
                heroVideoRef.current.playbackRate = 1.25;
              }
            }}
            onCanPlay={() => setVideoReady(true)}
            onPlay={() => setShowReplay(false)}
            onEnded={() => setShowReplay(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity: videoReady ? 1 : 0,
              transition: 'opacity 200ms ease',
            }}
          />
          {showReplay && (
            <button
              onClick={() => {
                const vid = heroVideoRef.current;
                if (vid) {
                  vid.currentTime = 0;
                  setShowReplay(false);
                  vid.play();
                }
              }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.45))',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: '0.03em',
              }}
            >
              ▶ Replay
            </button>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section
        style={{
          padding: '0 1.25rem 3rem',
          maxWidth: '1180px',
          margin: '0 auto',
          display: 'grid',
          gap: '1.5rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        }}
      >
        {[
          {
            title: 'Stay on top of your stash',
            body: 'Quantities, opened dates, reminders, and organized blends—never lose track.',
          },
          {
            title: 'Log every session',
            body: 'Ratings, notes, and photos that build your smoking journal over time.',
          },
          {
            title: 'Discover & connect',
            body: 'See what the community loves, find smoking spots, and share check-ins.',
          },
        ].map((item) => (
          <div
            key={item.title}
            className="card"
            style={{
              padding: '1.5rem',
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.02)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <h3
              style={{
                color: 'var(--color-accent)',
                fontFamily: 'Oswald, system-ui, sans-serif',
                fontSize: '1.25rem',
                margin: 0,
              }}
            >
              {item.title}
            </h3>
            <p style={{ color: '#cfcfcf', margin: 0, lineHeight: 1.6 }}>{item.body}</p>
          </div>
        ))}
      </section>

      {/* Testimonials */}
      <section style={{ padding: '0 1.25rem 3rem', maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: 0,
              textAlign: 'center',
            }}
          >
            Trusted by 200+ enthusiasts
          </h2>
          <span style={{ color: '#cfcfcf', fontSize: '0.95rem' }}>Community first.</span>
        </div>
        <div
          style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          {testimonials.map((t, idx) => (
            <div
              key={idx}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: '12px',
                padding: '1.25rem',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <p style={{ margin: '0 0 0.75rem', color: '#f5f5f5', lineHeight: 1.6 }}>{t.quote}</p>
              <div style={{ color: '#cfcfcf', fontWeight: 600 }}>{t.name}</div>
              <div style={{ color: '#8a8a8a', fontSize: '0.9rem' }}>{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '0 1.25rem 3rem', maxWidth: '1180px', margin: '0 auto' }}>
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <h2
            style={{
              fontSize: '1.5rem',
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: 0,
              textAlign: 'center',
            }}
          >
            FAQ
          </h2>
          <p style={{ color: '#cfcfcf', margin: '0.35rem 0 0' }}>Quick answers to common questions.</p>
        </div>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {faqs.map((faq) => (
            <div
              key={faq.question}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: '10px',
                padding: '1rem 1.25rem',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <div style={{ color: '#f5f5f5', fontWeight: 700, marginBottom: '0.35rem' }}>{faq.question}</div>
              <div style={{ color: '#cfcfcf', lineHeight: 1.6 }}>{faq.answer}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA repeat */}
      <section style={{ padding: '0 1.25rem 2.5rem', maxWidth: '860px', margin: '0 auto' }}>
        <div
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: '14px',
            padding: '1.5rem',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <h3
            style={{
              fontSize: '1.4rem',
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: '0 0 0.5rem',
              textAlign: 'center',
            }}
          >
            Ready to start your cellar?
          </h3>
          <p style={{ color: '#cfcfcf', margin: '0 0 1rem' }}>Log your blends, sessions, and reminders in seconds.</p>
          <Button variant="primary" size="large" onClick={scrollToLogin} style={{ padding: '0.85rem 1.25rem' }}>
            Get started
          </Button>
        </div>
      </section>

      {/* Login */}
      <section
        id="previewLogin"
        style={{
          padding: '0 1.25rem 3rem',
          maxWidth: '520px',
          margin: '0 auto',
        }}
      >
        <div
          className="card"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: '14px',
            padding: '1.75rem',
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <h3
            style={{
              textAlign: 'center',
              fontSize: '1.5rem',
              color: 'var(--color-secondary)',
              fontFamily: 'Oswald, system-ui, sans-serif',
              margin: '0 0 1rem',
            }}
          >
            Join Ember
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <Button type="button" variant="primary" fullWidth onClick={() => window.location.assign('/login')}>
              Log In
            </Button>
            <Button type="button" variant="secondary" fullWidth onClick={() => window.location.assign('/login')}>
              Create Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: '2rem 1rem',
          textAlign: 'center',
          borderTop: '1px solid var(--color-border)',
          color: '#999',
          fontSize: '0.9rem',
        }}
      >
        <p style={{ margin: 0 }}>© 2026 Ember. All rights reserved.</p>
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <Link to="/privacy" style={{ color: 'var(--color-accent)' }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: 'var(--color-accent)' }}>
            Terms
          </Link>
        </div>
      </footer>

      <style>{`
        .carousel-strip::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
